import { register } from "register-service-worker";

const APP_SHELL_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const APP_SHELL_REFRESH_KEY = "erp-app-shell-refresh-signature";
let controllerChangeReloadScheduled = false;

const unregisterLegacyServiceWorkers = async () => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map((registration) => registration.unregister()),
    );

    if ("caches" in window) {
      const cacheKeys = await window.caches.keys();
      await Promise.all(
        cacheKeys.map((cacheKey) => window.caches.delete(cacheKey)),
      );
    }
  } catch (error) {
    console.error("Error clearing service workers:", error);
  }
};

const normalizeAssetUrl = (value) => {
  try {
    const url = new URL(value, window.location.href);

    if (url.origin !== window.location.origin) {
      return "";
    }

    return `${url.pathname}${url.search}`;
  } catch (error) {
    return "";
  }
};

const uniqueSortedSignature = (values) =>
  Array.from(new Set(values.filter(Boolean))).sort().join("|");

const currentAssetSignature = () =>
  uniqueSortedSignature(
    Array.from(document.querySelectorAll('script[src],link[rel="stylesheet"][href]'))
      .map((node) => normalizeAssetUrl(node.src || node.href)),
  );

const assetSignatureFromHtml = (html) => {
  const assets = [];
  const pattern = /\b(?:src|href)="([^"]+\.(?:js|css)(?:\?[^"]*)?)"/g;
  let match;

  while ((match = pattern.exec(html)) !== null) {
    assets.push(normalizeAssetUrl(match[1]));
  }

  return uniqueSortedSignature(assets);
};

const appShellUrl = () => {
  const baseUrl = new URL(process.env.BASE_URL || "/", window.location.origin);
  baseUrl.pathname = `${baseUrl.pathname.replace(/\/?$/, "/")}index.html`;
  baseUrl.searchParams.set("_fresh", `${Date.now()}-${Math.random().toString(16).slice(2)}`);
  return baseUrl.toString();
};

const reloadForNewAppShell = (nextSignature) => {
  try {
    if (window.sessionStorage.getItem(APP_SHELL_REFRESH_KEY) === nextSignature) {
      return;
    }

    window.sessionStorage.setItem(APP_SHELL_REFRESH_KEY, nextSignature);
  } catch (error) {
    // sessionStorage can be blocked; freshness still wins.
  }

  window.location.reload();
};

const refreshIfAppShellChanged = async () => {
  const response = await fetch(appShellUrl(), {
    cache: "no-store",
    headers: {
      "Cache-Control": "no-store, no-cache, must-revalidate, max-age=0",
      Pragma: "no-cache",
    },
  });

  if (!response.ok) {
    return;
  }

  const currentSignature = currentAssetSignature();
  const nextSignature = assetSignatureFromHtml(await response.text());

  if (currentSignature && nextSignature && currentSignature !== nextSignature) {
    reloadForNewAppShell(nextSignature);
  }
};

const scheduleAppShellFreshnessChecks = () => {
  let running = false;
  const check = async () => {
    if (document.visibilityState === "hidden" || running) {
      return;
    }

    running = true;
    try {
      await refreshIfAppShellChanged();
    } catch (error) {
      // A transient network error must not block the ERP.
    } finally {
      running = false;
    }
  };

  window.setTimeout(check, 15000);
  window.setInterval(check, APP_SHELL_CHECK_INTERVAL_MS);
  window.addEventListener("focus", check);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      check();
    }
  });
};

const reloadAfterServiceWorkerUpdate = () => {
  if (controllerChangeReloadScheduled) {
    return;
  }

  controllerChangeReloadScheduled = true;
  window.location.reload();
};

const requestWaitingWorkerActivation = (registration) => {
  try {
    if (registration && typeof registration.update === "function") {
      registration.update();
    }

    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
  } catch (error) {
    // Service worker update checks are best-effort and must never stop startup.
  }
};

const isProduction = process.env.NODE_ENV === "production";
const pwaFlag = String(process.env.VUE_APP_ENABLE_PWA || "").toLowerCase();
const isPwaExplicitlyEnabled =
  pwaFlag === "true" || pwaFlag === "1" || pwaFlag === "on";
const shouldRegisterServiceWorkerCleanup = isProduction;

if (!shouldRegisterServiceWorkerCleanup) {
  unregisterLegacyServiceWorkers();
}

if (shouldRegisterServiceWorkerCleanup) {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      reloadAfterServiceWorkerUpdate,
    );
  }

  scheduleAppShellFreshnessChecks();

  register(`${process.env.BASE_URL}service-worker.js`, {
    registrationOptions: {
      scope: process.env.BASE_URL,
    },
    ready(registration) {
      requestWaitingWorkerActivation(registration);
      // console.log(
      //   'App is being served from cache by a service worker.\n' +
      //   'For more details, visit https://goo.gl/AFskqB'
      // )
    },
    registered(registration) {
      requestWaitingWorkerActivation(registration);
      // console.log('Service worker has been registered.')
    },
    cached() {
      // console.log('Content has been cached for offline use.')
    },
    updatefound(registration) {
      requestWaitingWorkerActivation(registration);
      // console.log('New content is downloading.')
    },
    updated(registration) {
      requestWaitingWorkerActivation(registration);

      if (!isPwaExplicitlyEnabled) {
        return;
      }

      try {
        const waiting =
          registration && registration.waiting ? registration.waiting : null;
        const key =
          waiting && waiting.scriptURL
            ? waiting.scriptURL
            : `sw-updated-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        window.dispatchEvent(
          new CustomEvent("pwa:update-available", {
            detail: { registration, key },
          }),
        );
      } catch (e) {
        // No-op: si el navegador no soporta CustomEvent o falla algo, no bloquear la app
      }
    },
    offline() {
      // Este sistema opera 100% online.
    },
    error(error) {
      console.error("Error during service worker registration:", error);
    },
  });
}
